import { appPath } from "../appPath";
import { formatChinaDateTime } from "../time";
import { useEffect, useState } from "react";

import {
  createPlatformSubaccount,
  disablePlatformSubaccount,
  enablePlatformSubaccount,
  getPlatformSubaccountCredentials,
  listPlatformSubaccounts,
  resetPlatformSubaccountPassword,
  updatePlatformSubaccount,
  updatePlatformSubaccountPermissions,
  type PlatformCredentials,
  type PlatformPermission,
  type PlatformSubaccount,
} from "./api";

const PAGE_SIZE = 10;

type AccountForm = {
  mode: "create" | "edit";
  account?: PlatformSubaccount;
  name: string;
  mobile: string;
  isAgent: boolean;
};

type Confirmation = {
  action: "reset" | "disable" | "enable";
  account: PlatformSubaccount;
};

type PermissionForm = {
  account: PlatformSubaccount;
  permissions: PlatformPermission[];
};

type AgentIdentityConfirmation = {
  action: "enable" | "disable";
  form: AccountForm;
};

const PLATFORM_PERMISSION_OPTIONS: Array<{
  value: PlatformPermission;
  label: string;
}> = [
  { value: "PROPERTY_ORDERS", label: "不动产订单" },
  { value: "EXCEPTION_ORDERS", label: "异常订单" },
  { value: "SUPPORT", label: "客服咨询" },
  { value: "LAWYER_IDENTITY_REVIEWS", label: "实名认证审核" },
  { value: "PERSONAL_LAWYERS", label: "个人律师" },
  { value: "PAYMENT_COLLECTIONS", label: "收款明细" },
];

export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function formatCredential(account: string, password: string, loginUrl: string): string {
  return `登录地址：${loginUrl}\n登录账号：${account}\n登录密码：${password}`;
}

export function agentIdentityChange(
  currentIsAgent: boolean,
  nextIsAgent: boolean,
): "enable" | "disable" | null {
  if (currentIsAgent === nextIsAgent) return null;
  return nextIsAgent ? "enable" : "disable";
}

function credentialsFromCreated(account: string, initialPassword: string): PlatformCredentials {
  return { account, password: initialPassword };
}

export function PlatformSubaccounts() {
  const [accounts, setAccounts] = useState<PlatformSubaccount[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [draftQuery, setDraftQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState<AccountForm | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [agentIdentityConfirmation, setAgentIdentityConfirmation] = useState<AgentIdentityConfirmation | null>(null);
  const [permissionForm, setPermissionForm] = useState<PermissionForm | null>(null);
  const [credentials, setCredentials] = useState<PlatformCredentials | null>(null);
  const [credentialsTitle, setCredentialsTitle] = useState("");
  const [modalError, setModalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState("");

  async function reload() {
    setLoading(true);
    setError("");
    try {
      const result = await listPlatformSubaccounts({ query, page, pageSize: PAGE_SIZE });
      setAccounts(result.items);
      setTotal(result.total);
      if (result.total > 0 && page > pageCount(result.total, PAGE_SIZE)) {
        setPage(pageCount(result.total, PAGE_SIZE));
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "账户列表加载失败");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, [page, query]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  function openCreate() {
    setModalError("");
    setForm({ mode: "create", name: "", mobile: "", isAgent: false });
  }

  function openEdit(account: PlatformSubaccount) {
    setModalError("");
    setForm({
      mode: "edit",
      account,
      name: account.name,
      mobile: account.mobile,
      isAgent: account.is_agent,
    });
  }

  async function submitForm(event: React.FormEvent) {
    event.preventDefault();
    if (!form || submitting) return;
    const currentIsAgent = form.mode === "edit" ? form.account?.is_agent ?? false : false;
    const agentChange = agentIdentityChange(currentIsAgent, form.isAgent);
    if (agentChange) {
      setModalError("");
      setAgentIdentityConfirmation({ action: agentChange, form });
      return;
    }
    setSubmitting(true);
    setModalError("");
    try {
      if (form.mode === "create") {
        const created = await createPlatformSubaccount({
          name: form.name,
          mobile: form.mobile,
          is_agent: form.isAgent,
        });
        setForm(null);
        setCredentials(credentialsFromCreated(created.account, created.initial_password));
        setCredentialsTitle("子账户创建成功");
      } else if (form.account) {
        await updatePlatformSubaccount(form.account.id, {
          name: form.name,
          mobile: form.mobile,
          is_agent: form.isAgent,
        });
        setForm(null);
        setToast("账户信息已保存");
      }
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "操作失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmAgentIdentity() {
    if (!agentIdentityConfirmation || submitting) return;
    const { form: confirmedForm } = agentIdentityConfirmation;
    setSubmitting(true);
    setModalError("");
    try {
      if (confirmedForm.mode === "create") {
        const created = await createPlatformSubaccount({
          name: confirmedForm.name,
          mobile: confirmedForm.mobile,
          is_agent: true,
        });
        setCredentials(credentialsFromCreated(created.account, created.initial_password));
        setCredentialsTitle("子账户创建成功");
      } else if (confirmedForm.account) {
        await updatePlatformSubaccount(confirmedForm.account.id, {
          name: confirmedForm.name,
          mobile: confirmedForm.mobile,
          is_agent: confirmedForm.isAgent,
        });
        setToast("账户信息已保存");
      }
      setAgentIdentityConfirmation(null);
      setForm(null);
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "账户信息保存失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  async function submitPermissions(event: React.FormEvent) {
    event.preventDefault();
    if (!permissionForm || submitting) return;
    setSubmitting(true);
    setModalError("");
    try {
      await updatePlatformSubaccountPermissions(
        permissionForm.account.id,
        permissionForm.permissions,
      );
      setPermissionForm(null);
      setToast("账户权限已保存");
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "权限保存失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  function togglePermission(permission: PlatformPermission) {
    if (!permissionForm) return;
    const selected = permissionForm.permissions.includes(permission);
    setPermissionForm({
      ...permissionForm,
      permissions: selected
        ? permissionForm.permissions.filter((item) => item !== permission)
        : [...permissionForm.permissions, permission],
    });
  }

  async function copyCredential(account: PlatformSubaccount) {
    setError("");
    try {
      const result = await getPlatformSubaccountCredentials(account.id);
      await writeCredential(result);
      setToast("登录账号和密码已复制");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "登录凭证复制失败");
    }
  }

  async function writeCredential(value: PlatformCredentials) {
    if (!navigator.clipboard?.writeText) throw new Error("当前浏览器不支持复制，请更换浏览器后重试");
    await navigator.clipboard.writeText(formatCredential(value.account, value.password, `${window.location.origin}${appPath("/")}`));
  }

  async function copyModalCredential() {
    if (!credentials) return;
    try {
      await writeCredential(credentials);
      setToast("登录账号和密码已复制");
      setCredentials(null);
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "登录凭证复制失败");
    }
  }

  async function confirmAction() {
    if (!confirmation || submitting) return;
    setSubmitting(true);
    setModalError("");
    try {
      if (confirmation.action === "reset") {
        const result = await resetPlatformSubaccountPassword(confirmation.account.id);
        setConfirmation(null);
        setCredentials(result);
        setCredentialsTitle("密码重置成功");
      } else {
        const action = confirmation.action;
        if (action === "disable") await disablePlatformSubaccount(confirmation.account.id);
        else await enablePlatformSubaccount(confirmation.account.id);
        setConfirmation(null);
        setToast(action === "disable" ? "账户已停用" : "账户已启用");
      }
      await reload();
    } catch (reason) {
      setModalError(reason instanceof Error ? reason.message : "操作失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  }

  function search(event: React.FormEvent) {
    event.preventDefault();
    setPage(1);
    setQuery(draftQuery.trim());
  }

  const pages = pageCount(total, PAGE_SIZE);

  return (
    <section className="platform-subaccounts" aria-label="平台子账户管理">
      <div className="platform-panel">
        <form className="platform-list-toolbar" onSubmit={search}>
          <div className="platform-list-search">
            <input
              aria-label="搜索子账户"
              placeholder="搜索账号ID、名称或手机号"
              value={draftQuery}
              onChange={(event) => setDraftQuery(event.target.value)}
            />
            <button type="submit">查询</button>
            {(query || draftQuery) && (
              <button
                className="is-secondary"
                type="button"
                onClick={() => {
                  setDraftQuery("");
                  setQuery("");
                  setPage(1);
                }}
              >
                重置
              </button>
            )}
          </div>
          <button className="platform-primary-button" type="button" onClick={openCreate}>
            <span aria-hidden="true">＋</span> 创建子账户
          </button>
        </form>

        <div className="platform-table-wrap">
          <table className="platform-account-table">
            <thead>
              <tr>
                <th>账号ID</th>
                <th>名称</th>
                <th>手机号</th>
                <th>身份</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {!loading && !error && accounts.map((account) => (
                <tr key={account.id}>
                  <td><strong>{account.account}</strong></td>
                  <td>{account.name}</td>
                  <td>{account.mobile}</td>
                  <td>{account.is_agent ? <span className="platform-status is-active">代理</span> : "普通"}</td>
                  <td>
                    <span className={`platform-status ${account.status === "ACTIVE" ? "is-active" : "is-disabled"}`}>
                      {account.status === "ACTIVE" ? "启用" : "停用"}
                    </span>
                  </td>
                  <td>{formatChinaDateTime(account.created_at)}</td>
                  <td>
                    <div className="platform-row-actions">
                      <button type="button" onClick={() => openEdit(account)}>编辑</button>
                      <button
                        type="button"
                        onClick={() => {
                          setModalError("");
                          setPermissionForm({ account, permissions: [...account.permissions] });
                        }}
                      >权限管理</button>
                      <button type="button" onClick={() => void copyCredential(account)}>复制登录凭证</button>
                      <button type="button" onClick={() => { setModalError(""); setConfirmation({ action: "reset", account }); }}>重置密码</button>
                      <button
                        className={account.status === "ACTIVE" ? "is-danger" : "is-positive"}
                        type="button"
                        onClick={() => {
                          setModalError("");
                          setConfirmation({ action: account.status === "ACTIVE" ? "disable" : "enable", account });
                        }}
                      >
                        {account.status === "ACTIVE" ? "停用" : "启用"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {loading && <tr><td className="platform-table-state" colSpan={7}>正在加载账户…</td></tr>}
              {!loading && error && <tr><td className="platform-table-state is-error" colSpan={7}>{error}<button type="button" onClick={() => void reload()}>重新加载</button></td></tr>}
              {!loading && !error && accounts.length === 0 && <tr><td className="platform-table-state" colSpan={7}>{query ? "没有找到匹配的子账户" : "暂无平台子账户"}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="platform-pagination">
          <span>共 {total} 条，每页 {PAGE_SIZE} 条</span>
          <div>
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>上一页</button>
            <span>{page} / {pages}</span>
            <button type="button" disabled={page >= pages || loading} onClick={() => setPage((value) => value + 1)}>下一页</button>
          </div>
        </div>
      </div>

      {form && (
        <div className="platform-modal-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setForm(null); }}>
          <form className="platform-modal" role="dialog" aria-modal="true" aria-labelledby="account-form-title" onSubmit={submitForm}>
            <div className="platform-modal-heading">
              <h2 id="account-form-title">{form.mode === "create" ? "创建平台子账户" : "编辑子账户"}</h2>
              <button aria-label="关闭" type="button" onClick={() => setForm(null)}>×</button>
            </div>
            <label>名称<input autoFocus maxLength={64} placeholder="请输入个人姓名或公司名称" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
            <label>手机号<input inputMode="numeric" maxLength={11} placeholder="请输入11位手机号" value={form.mobile} onChange={(event) => setForm({ ...form, mobile: event.target.value.replace(/\D/g, "") })} /></label>
            <label className="platform-permission-option">
              <input type="checkbox" checked={form.isAgent} onChange={(event) => setForm({ ...form, isAgent: event.target.checked })} />
              <span>该账户为代理</span>
            </label>
            {modalError && <p className="platform-modal-error">{modalError}</p>}
            <div className="platform-modal-actions">
              <button className="is-secondary" type="button" onClick={() => setForm(null)}>取消</button>
              <button className="platform-primary-button" type="submit" disabled={submitting || form.name.trim().length < 2 || form.mobile.length !== 11}>{form.mode === "create" ? "创建" : "保存"}</button>
            </div>
          </form>
        </div>
      )}

      {confirmation && (
        <div className="platform-modal-layer" role="presentation">
          <section className="platform-modal platform-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="confirmation-title">
            <h2 id="confirmation-title">{confirmation.action === "reset" ? "确认重置密码" : confirmation.action === "disable" ? "确认停用账户" : "确认启用账户"}</h2>
            <p>
              {confirmation.action === "reset" && <>确定重置 <strong>{confirmation.account.name}</strong> 的登录密码吗？重置后原密码立即失效。</>}
              {confirmation.action === "disable" && <>确定停用 <strong>{confirmation.account.name}</strong> 吗？该账户将立即退出登录且无法再次登录。</>}
              {confirmation.action === "enable" && <>确定重新启用 <strong>{confirmation.account.name}</strong> 吗？</>}
            </p>
            {modalError && <p className="platform-modal-error">{modalError}</p>}
            <div className="platform-modal-actions">
              <button className="is-secondary" type="button" onClick={() => setConfirmation(null)}>取消</button>
              <button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void confirmAction()}>确定</button>
            </div>
          </section>
        </div>
      )}

      {agentIdentityConfirmation && (
        <div className="platform-modal-layer" role="presentation">
          <section className="platform-modal platform-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="agent-identity-confirmation-title">
            <h2 id="agent-identity-confirmation-title">
              {agentIdentityConfirmation.form.mode === "create"
                ? "确认创建代理子账户"
                : agentIdentityConfirmation.action === "enable"
                  ? "确认设为代理"
                  : "确认取消代理身份"}
            </h2>
            <p>
              {agentIdentityConfirmation.form.mode === "create"
                ? <>确定以代理身份创建 <strong>{agentIdentityConfirmation.form.name}</strong> 吗？</>
                : agentIdentityConfirmation.action === "enable"
                ? <>确定将 <strong>{agentIdentityConfirmation.form.name}</strong> 设为代理吗？</>
                : <>确定取消 <strong>{agentIdentityConfirmation.form.name}</strong> 的代理身份吗？</>}
            </p>
            {modalError && <p className="platform-modal-error">{modalError}</p>}
            <div className="platform-modal-actions">
              <button className="is-secondary" type="button" disabled={submitting} onClick={() => setAgentIdentityConfirmation(null)}>取消</button>
              <button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void confirmAgentIdentity()}>确认</button>
            </div>
          </section>
        </div>
      )}

      {permissionForm && (
        <div
          className="platform-modal-layer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setPermissionForm(null);
          }}
        >
          <form
            className="platform-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="permission-form-title"
            onSubmit={submitPermissions}
          >
            <div className="platform-modal-heading">
              <h2 id="permission-form-title">权限管理</h2>
              <button aria-label="关闭" type="button" onClick={() => setPermissionForm(null)}>×</button>
            </div>
            <p>为 <strong>{permissionForm.account.name}</strong> 开通可使用的平台管理模块。</p>
            <div className="platform-permission-options">
              {PLATFORM_PERMISSION_OPTIONS.map((option) => (
                <label className="platform-permission-option" key={option.value}>
                  <input
                    type="checkbox"
                    checked={permissionForm.permissions.includes(option.value)}
                    onChange={() => togglePermission(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {modalError && <p className="platform-modal-error">{modalError}</p>}
            <div className="platform-modal-actions">
              <button className="is-secondary" type="button" onClick={() => setPermissionForm(null)}>取消</button>
              <button className="platform-primary-button" type="submit" disabled={submitting}>保存</button>
            </div>
          </form>
        </div>
      )}

      {credentials && (
        <div className="platform-modal-layer" role="presentation">
          <section className="platform-modal platform-credential-modal" role="dialog" aria-modal="true" aria-labelledby="credential-title">
            <h2 id="credential-title">{credentialsTitle}</h2>
            <p>登录凭证已生成。密码不会在列表中明文显示，可随时通过“复制登录凭证”再次获取。</p>
            <dl>
              <div><dt>登录账号</dt><dd>{credentials.account}</dd></div>
              <div><dt>登录密码</dt><dd>••••••••••••</dd></div>
            </dl>
            {modalError && <p className="platform-modal-error">{modalError}</p>}
            <div className="platform-modal-actions">
              <button className="is-secondary" type="button" onClick={() => setCredentials(null)}>关闭</button>
              <button className="platform-primary-button" type="button" onClick={() => void copyModalCredential()}>复制账号和密码</button>
            </div>
          </section>
        </div>
      )}

      {toast && <div className="platform-toast" role="status">{toast}</div>}
    </section>
  );
}
